import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Eye, EyeOff, ArrowRight, ShieldCheck, Zap, Gauge, Cpu } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18n';
import api from '../lib/api';

const LOGO = '/speed-motors-logo.png';
const PANEL_IMG = '/showroom.jpg';

const langs = [
  { code: 'pt', flag: '🇧🇷' },
  { code: 'en', flag: '🇺🇸' },
  { code: 'es', flag: '🇪🇸' },
];

const LoginPage = () => {
  const navigate = useNavigate();
  const { user, loading, setUser } = useAuth();
  const { t, lang, setLang } = useI18n();
  const [showPass, setShowPass] = useState(false);
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && user) navigate('/app', { replace: true });
  }, [user, loading, navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    if (!loginId || !password) { setError('Informe login e senha.'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/auth/login', { login: loginId, password });
      setUser(data);
      navigate('/app', { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === 'string' ? d : 'Falha no login. Tente novamente.');
    } finally { setSubmitting(false); }
  };

  return (
    <div className="min-h-screen w-full bg-[#05070c] text-white flex overflow-hidden relative">
      {/* background image full bleed */}
      <img src={PANEL_IMG} alt="Speed Motors Showroom" className="absolute inset-0 w-full h-full object-cover" />
      <div className="absolute inset-0 bg-black/60" />
      <div className="absolute inset-0 bg-gradient-to-r from-[#05070c] via-[#05070c]/70 to-transparent" />
      <div className="absolute inset-0 aurora opacity-70 pointer-events-none" />
      <div className="absolute inset-0 tech-grid opacity-40 pointer-events-none" />
      {/* animated scan beam */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute left-0 right-0 h-24 bg-gradient-to-b from-transparent via-[#3b82f6]/10 to-transparent animate-scan" />
      </div>
      {/* top load bar */}
      <div className="absolute top-0 left-0 right-0 h-[3px] z-30 overflow-hidden">
        <div className="h-full w-1/2 bg-gradient-to-r from-transparent via-[#3b82f6] to-[#22d3ee] animate-loadbar" />
      </div>

      {/* language toggle */}
      <div className="absolute top-6 right-6 z-30 flex gap-1.5">
        {langs.map((l) => (
          <button
            key={l.code}
            onClick={() => setLang(l.code)}
            className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-[11px] font-semibold border transition-all duration-200 ${lang === l.code ? 'border-[#3b82f6]/60 bg-[#3b82f6]/15 text-white shadow-glow' : 'border-white/10 text-white/50 hover:text-white/90 hover:border-white/25'}`}
          >
            <span>{l.flag}</span>{l.code.toUpperCase()}
          </button>
        ))}
      </div>

      {/* CONTENT */}
      <div className="relative z-20 w-full flex flex-col lg:flex-row items-center">
        {/* LEFT — FORM */}
        <div className="w-full lg:w-[46%] flex items-center justify-center px-6 sm:px-12 py-12 min-h-screen">
          <div className="w-full max-w-[420px] animate-fade-up">
            {/* brand */}
            <div className="flex items-center gap-3 mb-10">
              <div className="relative">
                <img src={LOGO} alt="Speed Motors" className="w-12 h-12 rounded-full object-cover ring-1 ring-white/15 relative z-10" />
                <span className="absolute inset-0 rounded-full animate-glow-pulse" />
              </div>
              <div className="leading-tight">
                <p className="font-display font-bold text-base tracking-[0.14em]">SPEED MOTORS</p>
                <p className="text-[9px] tracking-[0.32em] text-[#60a5fa]/80 uppercase">{t('dealerSuite')}</p>
              </div>
            </div>

            {/* glass card */}
            <div className="glass-strong rounded-2xl p-7 sm:p-9 shadow-glow-lg relative overflow-hidden">
              <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#3b82f6] to-transparent" />
              <div className="flex items-center gap-2 mb-4">
                <ShieldCheck className="w-4 h-4 text-[#3b82f6]" />
                <span className="text-[10px] tracking-[0.3em] text-white/45 font-semibold uppercase">{t('restricted')}</span>
              </div>
              <h2 className="font-display font-bold text-3xl sm:text-[38px] leading-[0.98] tracking-tight mb-2">
                <span className="text-white">{t('welcome')}</span><br />
                <span className="text-gradient-blue">{t('welcome2')}</span>
              </h2>
              <span className="block h-[3px] w-12 rounded bg-gradient-to-r from-[#3b82f6] to-[#22d3ee] mt-4 mb-8 shadow-glow" />

              <form onSubmit={handleLogin} className="space-y-5">
                <div>
                  <label className="block text-[11px] tracking-[0.2em] text-white/50 font-semibold mb-2 uppercase">Login</label>
                  <input
                    type="text"
                    data-testid="login-input"
                    value={loginId}
                    onChange={(e) => setLoginId(e.target.value)}
                    placeholder="seu.login"
                    autoComplete="username"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/25 outline-none focus:border-[#3b82f6] focus:bg-white/[0.06] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] transition-all duration-200"
                  />
                </div>
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <label className="block text-[11px] tracking-[0.2em] text-white/50 font-semibold uppercase">{t('password')}</label>
                    <button type="button" onClick={() => setShowPass(!showPass)} className="flex items-center gap-1 text-[11px] tracking-[0.12em] text-white/45 hover:text-[#60a5fa] transition-colors">
                      {showPass ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />} {t('show')}
                    </button>
                  </div>
                  <input
                    type={showPass ? 'text' : 'password'}
                    data-testid="password-input"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="••••••••"
                    autoComplete="current-password"
                    className="w-full bg-white/[0.04] border border-white/10 rounded-xl px-4 py-3.5 text-white placeholder:text-white/25 outline-none focus:border-[#3b82f6] focus:bg-white/[0.06] focus:shadow-[0_0_0_3px_rgba(59,130,246,0.15)] transition-all duration-200"
                  />
                </div>

                {error && <p data-testid="login-error" className="text-sm text-red-300 bg-red-500/10 border border-red-500/25 rounded-xl px-3.5 py-2.5">{error}</p>}

                <button
                  type="submit"
                  data-testid="login-submit-btn"
                  disabled={submitting}
                  className="group w-full bg-gradient-to-r from-[#2563eb] to-[#3b82f6] hover:from-[#1d4ed8] hover:to-[#2563eb] disabled:opacity-60 text-white font-bold tracking-[0.16em] text-sm py-4 rounded-xl flex items-center justify-center gap-2 transition-all duration-200 shadow-glow hover:shadow-glow-lg hover:scale-[1.015] uppercase"
                >
                  {submitting ? 'Entrando...' : t('enter')} <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                </button>
              </form>
            </div>

            <p className="text-center text-[10px] tracking-[0.22em] text-white/25 mt-7">© 2026 {t('suite')}</p>
          </div>
        </div>

        {/* RIGHT — HERO COPY (desktop) */}
        <div className="hidden lg:flex flex-1 flex-col justify-center px-10 xl:px-20 py-12">
          <div className="max-w-[560px] animate-fade-up" style={{ animationDelay: '0.15s' }}>
            <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/40 backdrop-blur px-3.5 py-1.5 mb-7">
              <span className="w-2 h-2 rounded-full bg-emerald-400 text-emerald-400 animate-pulse-dot" />
              <span className="text-[11px] tracking-[0.24em] text-white/70 font-semibold uppercase">{t('online')}</span>
            </div>
            <div className="flex items-center gap-3 mb-5">
              <span className="h-[2px] w-10 bg-gradient-to-r from-[#3b82f6] to-[#22d3ee]" />
              <span className="text-[11px] tracking-[0.3em] font-semibold text-[#60a5fa] uppercase">{t('tagline')}</span>
            </div>
            <h1 className="font-display font-bold leading-[0.94] tracking-tight text-[52px] xl:text-[68px] drop-shadow-[0_4px_20px_rgba(0,0,0,0.8)]">
              <span className="block text-white">{t('heroTitle1')}</span>
              <span className="block text-gradient-hero">{t('heroTitle2')}</span>
            </h1>
            <p className="mt-6 text-white/55 text-lg leading-relaxed max-w-[440px]">{t('heroSub')}</p>

            <div className="grid grid-cols-3 gap-3 max-w-[520px] mt-10 stagger">
              {[
                { icon: Zap, label: t('inventory').toUpperCase() },
                { icon: Cpu, label: 'CRM · LEADS' },
                { icon: Gauge, label: t('finance').toUpperCase() },
              ].map((c, i) => {
                const Ic = c.icon;
                return (
                  <div key={i} className="glass rounded-xl px-4 py-4 flex flex-col gap-3 glow-border hover:border-white/20 transition-colors">
                    <Ic className="w-6 h-6 text-[#60a5fa]" />
                    <span className="text-[10px] tracking-[0.2em] text-white/55 font-semibold">{c.label}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
